import { AxiosService } from '../../axios/axios.service';
import { tAxiosCustomConfig } from '../../axios/axios.types.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import {
  tAuthenticateWhatsappAPIParams,
  tDashboardRequest,
  tDashboardResponse,
  tFbDisconnectResponse,
  tLeadsByFunnel,
  tWapiConnectionResponse,
} from './Dashboard.types';

export const DashboardService = {
  getDashboard: async function (
    request: tDashboardRequest,
  ): Promise<tDashboardResponse> {
    try {
      const response = await AxiosService.get<tDashboardResponse>(
        `/dashboard/${request.workspaceId}`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * Resolves with the parsed `ConnectResponse`. INT-1516: a 200 no longer
   * means "connected". In `enforce` the body says whether Meta has activated
   * the record, so callers read it through `readConnectVerdict` instead of
   * inferring success from the status. Rejects with the raw axios error
   * (INT-914), so `response.data.code` survives to the caller.
   */
  authenticateWhatsappAPI: async function (
    params: tAuthenticateWhatsappAPIParams,
  ): Promise<tWapiConnectionResponse> {
    try {
      const { code, workspaceId, wabaId, phoneNumberId } = params;
      const config: tAxiosCustomConfig = {
        headers: {},
        isLegacy: true,
      };
      // waba_id / phone_number_id come from the WA_EMBEDDED_SIGNUP sessionInfo
      // and are authoritative for the backend (required for Coexistence, whose
      // token does not carry the WABA in granular_scopes). Sent as empty
      // strings when unknown — the backend treats "" as absent and falls back
      // to debug_token.
      //
      // AxiosService.post resolves with `response.data`, not the axios
      // response, so this is the body itself.
      const body = await AxiosService.post<
        {
          code: string;
          workspace_id: string;
          waba_id: string;
          phone_number_id: string;
        },
        tWapiConnectionResponse
      >(
        'whatsapp-api/account',
        {
          code,
          workspace_id: workspaceId,
          waba_id: wabaId ?? '',
          phone_number_id: phoneNumberId ?? '',
        },
        config,
      );

      LLog.info(
        {
          component: LOG_COMPONENTS.API,
          method: 'whatsapp-api',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'FB Connection request answered',
        { activation_state: body?.activation_state ?? 'absent' },
      );

      return body;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  disconnectAndUnsubscribe: async (
    pageId: string,
  ): Promise<tFbDisconnectResponse> => {
    const config: tAxiosCustomConfig = {
      headers: {},
      isLegacy: true,
    };
    try {
      return await AxiosService.delete<tFbDisconnectResponse>(
        `meta-messenger/unsubscribe-page?uuid=${pageId}`,
        config,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getLeadsByFunnel: async function (
    workspaceId: string,
  ): Promise<tLeadsByFunnel[]> {
    try {
      const response = await AxiosService.get<{
        data: { funnels: tLeadsByFunnel[] };
      }>(`/workspace/${workspaceId}/pipes/lead-count`);
      return Promise.resolve(response.data.funnels);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
